/**
 * `useWorkspace` — the dashboard bootstrap query.
 *
 * Fetches `GET /v1/workspace` (see `references/api-surface.md` §Workspace).
 * Returns the authenticated user + every org they belong to, zipped
 * with Clerk data (name, slug) and Supabase billing columns.
 *
 * Gated on `useAuth().isSignedIn` because the endpoint is JWT-only —
 * calling it pre-auth would return a 401 and force a sign-in bounce.
 * The `AuthGate` in `_app` already ensures the signed-in state by the
 * time any `/` route renders, but this belt-and-braces keeps the
 * hook usable in anywhere-else (e.g. `/onboarding`, which lives
 * outside `_app`).
 *
 * Query key is scoped by the active JWT-claim org id so the cache
 * drops cleanly on org-switch / token rotation. See
 * `query-keys.ts:workspaceKeys.current` for why.
 */
import { useAuth } from '@clerk/react';
import type { QueryClient } from '@tanstack/react-query';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api';
import type { WorkspaceResponse } from '../api-types';
import { useActiveOrg } from './active-org';
import { workspaceKeys } from './query-keys';

export function useWorkspace(): UseQueryResult<WorkspaceResponse> {
  const { isSignedIn, isLoaded } = useAuth();
  const { orgId } = useActiveOrg();
  return useQuery({
    queryKey: workspaceKeys.current(orgId),
    queryFn: () => apiFetch<WorkspaceResponse>('/v1/workspace'),
    enabled: isLoaded && !!isSignedIn,
  });
}

export function invalidateWorkspace(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: workspaceKeys.all });
}
